import { Box, Slide } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Role } from '@fullstory/proto/fs/services/orgs';
import { Notification } from '@fullstory/proto/fs/services/pinguin';
import { useMounted } from '@fullstory/react-hooks';
import { getSeatRole } from '@fullstory/redux/modules/fullstory/selectors';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NotificationItem } from '../NotificationItem';
import css from './index.module.scss';

export const DEFAULT_AUTO_DISMISS = 4500;
export interface NotificationPopoverProps
  extends Pick<React.ComponentProps<typeof NotificationItem>, 'notification' | 'onClickCta'> {
  autoDismiss?: boolean;
  onDismiss: (notificaiton: Notification) => void;
}

export const NotificationPopover: React.FC<NotificationPopoverProps> = ({
  notification,
  autoDismiss = true,
  onDismiss,
  onClickCta,
}) => {
  const [open, setOpen] = useState<boolean>(true);
  const isMounted = useMounted();
  const userRole = useSelector(getSeatRole);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoDismiss && isMounted()) {
      setTimeout(() => {
        onDismiss(notification);
        setOpen(false);
      }, DEFAULT_AUTO_DISMISS);
    }
  }, [autoDismiss, isMounted, onDismiss, notification]);

  const cta = autoDismiss
    ? intl.formatMessage({ defaultMessage: 'Dismiss', id: 'TDaF6J' })
    : userRole === Role.ADMIN
    ? intl.formatMessage({ defaultMessage: 'Acknowledge', id: 'a+0Mbo' })
    : intl.formatMessage({ defaultMessage: 'OK', id: 'kAEQyV' });

  return (
    <Slide data-component="NotificationPopover" position="right" in={open} nodeRef={nodeRef}>
      <Box
        className={css.NotificationPopover}
        width={506}
        testId="notification-popover"
        ref={nodeRef}
      >
        <NotificationItem
          notification={notification}
          onClickCta={onClickCta}
          closeProps={{
            onClose: () => {
              setOpen(false);
              // Let the useEffect handle the onDismiss call for autoDismissed notifications and just close the popover.
              // Calling onDismiss updates local storage that can cause other autoDismissed popovers to close depending
              // on the timestamp of this notification, which we don't want.
              if (!autoDismiss) {
                onDismiss(notification);
              }
            },
            cta,
          }}
        />
      </Box>
    </Slide>
  );
};
